import { useMemo, useState } from 'react';
import uniqBy from 'lodash/uniqBy';

import nullSafeItems from '@lyearn/core/utils/array/nullSafeItems';
import { getMatchingOptionsInSortedOrder } from '@lyearn/core/utils/search';
import { useTranslation } from '@lyearn/i18n';
import { useUserContext } from '@/modules/auth/hooks/useUser';
import { SkillDetailsFragment } from '@/modules/skill-matrix/graphql/fragment/generated/SkillDetails';
import { useAllSkillsListQuery } from '@/modules/skill-matrix/graphql/queries/generated/allSkills';
import { SkillTypeEnum } from '@/types/schema';

import { useUserFeedbackSkillsQuery } from '../graphql/queries/generated/userSkills';

const useRelatedSkills = ({
  skillIds,
  userId,
  isMindset,
  pause,
}: {
  skillIds: string[];
  userId?: string;
  isMindset?: boolean;
  pause?: boolean;
}) => {
  const { t } = useTranslation();
  const user = useUserContext();
  const [searchText, setSearchText] = useState('');
  const [{ data: skillData, fetching }] = useAllSkillsListQuery({
    variables: {
      filters: {
        ids: skillIds,
        types: [SkillTypeEnum.Ability, SkillTypeEnum.Behavior],
      },
      first: -1,
    },
    pause: pause || !skillIds.length,
  });
  const selectedSkills = useMemo(
    () => skillData?.skills.edges?.map((skillNode) => skillNode.node),
    [skillData?.skills.edges],
  );

  const [{ data }] = useUserFeedbackSkillsQuery({
    variables: {
      filters: {
        userId: userId || user.id,
      },
    },
  });

  const responsibilities = useMemo(() => {
    return data?.user?.responsibilities ?? [];
  }, [data?.user?.responsibilities]);

  const skills = useMemo(() => {
    const skillOrMindset = uniqBy(
      nullSafeItems(
        (isMindset
          ? responsibilities.flatMap((responsibility) => responsibility.mindsets)
          : responsibilities.flatMap(
              (responsibility) => responsibility.skills,
            )) as SkillDetailsFragment[],
      ),
      'id',
    );
    const skillNames = skillOrMindset?.map((skill) => skill?.name) ?? [];
    const matchedSkills = getMatchingOptionsInSortedOrder(skillNames ?? [], searchText);

    return searchText
      ? skillOrMindset?.filter((skill) => matchedSkills.includes(skill?.name))
      : skillOrMindset;
  }, [isMindset, responsibilities, searchText]);

  return {
    skills,
    selectedSkills: skillIds.length ? selectedSkills : [],
    fetching,
    searchText,
    setSearchText,
  };
};

export default useRelatedSkills;
